import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const t = language === 'en' ? {
    title: "Forgot Password",
    subtitle: "Enter your email address and we'll send you a link to reset your password.",
    email: "Email Address",
    submit: "Send Reset Link",
    loading: "Sending...",
    sent: "Reset Link Sent!",
    sentMsg: "If an account with that email exists, we've sent a password reset link. Please check your inbox and spam folder.",
    backLogin: "Back to Sign In",
    noAccount: "Don't have an account?",
    register: "Create Profile",
  } : {
    title: "Mot de Passe Oublié",
    subtitle: "Entrez votre adresse courriel et nous vous enverrons un lien pour réinitialiser votre mot de passe.",
    email: "Adresse courriel",
    submit: "Envoyer le Lien",
    loading: "Envoi...",
    sent: "Lien Envoyé !",
    sentMsg: "Si un compte avec cet courriel existe, nous avons envoyé un lien de réinitialisation. Vérifiez votre boîte de réception et le dossier spam.",
    backLogin: "Retour à la Connexion",
    noAccount: "Vous n'avez pas de compte?",
    register: "Créer un Profil",
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="bg-[#D4AF37] py-4">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <Link to="/">
            <img src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/xsd39lyf_IMG-20260121-WA0012.jpg" alt="TALITHA KOUMI TUG" className="h-12 w-auto" />
          </Link>
          <Link to="/login" className="text-white hover:underline font-medium flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> {t.backLogin}
          </Link>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
          {sent ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h1 className="text-2xl font-bold text-[#1D1D1D] mb-2">{t.sent}</h1>
              <p className="text-gray-600 text-sm mb-6">{t.sentMsg}</p>
              <Link to="/login" data-testid="back-to-login-link" className="text-[#CD0000] font-bold hover:underline">
                {t.backLogin}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-[#1D1D1D] mb-2">{t.title}</h1>
              <p className="text-gray-600 mb-6 text-sm">{t.subtitle}</p>

              {error && (
                <div data-testid="forgot-error" className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {error}
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="forgot-password-form">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.email}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      data-testid="forgot-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="forgot-submit-button"
                  disabled={loading}
                  className="w-full bg-[#CD0000] text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-[#c91560] transition-colors disabled:opacity-50"
                >
                  {loading ? t.loading : t.submit} <ArrowRight className="w-5 h-5" />
                </button>
              </form>

              <p className="text-center mt-6 text-gray-600 text-sm">
                {t.noAccount}{' '}
                <Link to="/register" className="text-[#CD0000] font-medium hover:underline">{t.register}</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
